/**
 * Application configuration
 * Uses environment variables with fallbacks
 */

interface AppConfig {
  apiBaseUrl: string;
  authServiceUrl: string;
  authServiceAdminUrl: string;
  brandServiceUrl: string;
  analyticsServiceUrl: string;
  appUrl: string;
  authProviders: string;
  appId: string;
}

/**
 * Get the API base URL based on environment
 * In production, if VITE_API_BASE_URL is empty, it uses the same domain as the frontend
 */
const getApiBaseUrl = (): string => {
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;

  // If explicitly set in environment, use it
  if (envApiUrl) {
    return envApiUrl;
  }

  // In production with Nginx reverse proxy, use relative path (same domain)
  // This works because Nginx proxies /api/ to the backend
  if (import.meta.env.PROD) {
    return window.location.origin;
  }

  // Fallback for development
  return 'http://localhost:5000';
};

/**
 * Get the Auth Service URL based on environment
 */
const getAuthServiceUrl = (): string => {
  const envAuthUrl = import.meta.env.VITE_AUTH_SERVICE_URL;

  if (envAuthUrl) {
    return envAuthUrl;
  }

  // Fallback for development
  return 'http://localhost:5500';
};

/**
 * Get the Auth Service Admin Portal URL
 * This is the centralized admin portal for user and OAuth client management
 */
const getAuthServiceAdminUrl = (): string => {
  const envAdminUrl = import.meta.env.VITE_AUTH_SERVICE_ADMIN_URL;

  if (envAdminUrl) {
    return envAdminUrl;
  }

  // In development, admin portal runs on its own Vite dev server
  if (import.meta.env.DEV) {
    return 'http://localhost:5173/admin';
  }

  // In production, admin portal is served by AuthService
  return getAuthServiceUrl() + '/admin';
};

/**
 * Get the Brand Service URL based on environment
 */
const getBrandServiceUrl = (): string => {
  const envBrandUrl = import.meta.env.VITE_API_BRAND_SERVICE_URL;

  if (envBrandUrl) {
    return envBrandUrl;
  }

  // Fallback for development
  return 'http://localhost:5002';
};

/**
 * Get the Analytics Service URL based on environment
 */
const getAnalyticsServiceUrl = (): string => {
  const envAnalyticsUrl = import.meta.env.VITE_ANALYTICS_SERVICE_URL;

  if (envAnalyticsUrl) {
    return envAnalyticsUrl;
  }

  // Fallback for development
  return 'http://localhost:5001';
};

/**
 * Get enabled OAuth providers (comma-separated list)
 * Default to 'google,microsoft' for sqrone
 */
const getAuthProviders = (): string => {
  const envProviders = import.meta.env.VITE_AUTH_PROVIDERS;

  if (envProviders) {
    return envProviders;
  }

  // Default to Google and Microsoft for sqrone
  return 'google,microsoft';
};

/**
 * Get the app identifier for product mapping in AuthService
 * Default to 'sqrone' for this application
 */
const getAppId = (): string => {
  const envAppId = import.meta.env.VITE_APP_ID;

  if (envAppId) {
    return envAppId;
  }

  // Default to 'sqrone' for this application
  return 'sqrone';
};

const config: AppConfig = {
  apiBaseUrl: getApiBaseUrl(),
  authServiceUrl: getAuthServiceUrl(),
  authServiceAdminUrl: getAuthServiceAdminUrl(),
  brandServiceUrl: getBrandServiceUrl(),
  analyticsServiceUrl: getAnalyticsServiceUrl(),
  appUrl: window.location.origin,
  authProviders: getAuthProviders(),
  appId: getAppId(),
};

export default config;
